<template>
  <RecentPages/>
  <div class="app-container">
    <sidebar-menu :menu="menu" :smoothScroll="true" :collapsed="sidebarCollapsed" @update:collapsed="handleSidebarToggle"
      :show-one-child="true" :hidden-toggle="false">
      <template v-slot:header>
        <h3 :class="{ 'd-none': sidebarCollapsed }" class="text-center text-light py-2">Treasury</h3>
      </template>
    </sidebar-menu>

    <div class="main-content" :class="{ 'collapsed': sidebarCollapsed }">
      <nav class="navbar navbar-light bg-white border-bottom" :class="{ 'collapsed': sidebarCollapsed }">
        <div class="container-fluid">
          <h5 class="m-0">{{ currentRouteTitle }}</h5>
          <div class="ms-auto">
            <i class="bi bi-bell mx-3"></i>
            <i class="bi bi-person-circle"></i>
          </div>
        </div>
      </nav>

      <div class="content-container p-4">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getUserRoles } from '@/utils/auth'
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import RecentPages from "@/components/customs/RecentPages.vue"

const route = useRoute()
const sidebarCollapsed = ref(false)

// Load saved state from localStorage
onMounted(() => {
  const savedState = localStorage.getItem('sidebarCollapsed')
  if (savedState !== null) {
    sidebarCollapsed.value = JSON.parse(savedState)
  }
})

const handleSidebarToggle = (collapsed) => {
  sidebarCollapsed.value = collapsed;
  localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed))
}

const menu = computed(() => {
  const allItems = [
    {
      href: '/',
      title: 'Dashboard',
      icon: 'bi bi-speedometer',
      exact: true,
      trackRecent: true,
      roles: ['admin', 'user', 'cashier'] // Example roles who can access
    },
    {
      title: 'Collections',
      icon: 'bi bi-receipt',
      roles: ['admin', 'cashier','teller','collector'], // Only these roles can see Collections
      exact: true,
      child: [
        {
          href: '/collections/ctc',
          title: 'Community Tax Certificate',
          icon: 'bi bi-newspaper',
         roles: ['admin', 'cashier','teller','collector'],
        },
        {
          href: '/collections/cashticket',
          title: 'Cash Ticket',
          icon: 'bi bi-ticket-detailed',
          roles: ['admin', 'cashier']
        },
        {
          title: 'FORM51',
          icon: 'bi bi-pie-chart-fill',
          roles: ['admin', 'cashier','teller','collector'],
          child: [
            {
              href: '/collections/water',
              title: 'Water Collection',
              icon: 'bi bi-droplet-half',
              roles: ['admin', 'cashier']
            },
            {
              href: '/collections/business',
              title: 'Business Collection',
              icon: 'bi bi-briefcase-fill',
              roles: ['admin', 'cashier']
            },
            {
              href: '/collections/general',
              title: 'General Collection',
              icon: 'bi bi-collection',
              roles: ['admin', 'cashier','teller','collector'],
            },
            {
              href: '/collections/market',
              title: 'Market Stall Rental',
              icon: 'bi bi-shop',
              roles: ['admin', 'cashier']
            }
          ]
        },
        {
          href: '/collections/lct',
          title: 'Large Cattle Transfer',
          icon: 'bi bi-truck',
          roles: ['admin', 'agriculture']
        },
        {
          href: '/collections/lco',
          title: 'Large Cattle Ownership',
          icon: 'bi bi-truck-flatbed',
          roles: ['admin', 'agriculture']
        },
        {
          href: '/collections/mlf',
          title: 'Marriage License Fee',
          icon: 'bi bi-heart-fill',
          roles: ['admin', 'registrar']
        },
        {
          href: '/collections/rptax',
          title: 'Real Property',
          icon: 'bi bi-map',
          roles: ['admin', 'assessor']
        },
        {
          href: '/collections/slaughter',
          title: 'Slaughter Permit',
          icon: 'bi bi-droplet', // More appropriate icon
          roles: ['admin', 'health']
        }
      ]
    },
    {
      title: 'Reports',
      icon: 'bi bi-graph-up-arrow',
      roles: ['admin', 'supervisor'],
      child: [
        {
          title: 'Abstract',
          icon: 'bi bi-pie-chart-fill',
          roles: ['admin', 'supervisor'],
          child: [
            {
              href: '/reports/abstract/rptax',
              title: 'RPT Collections',
              icon: 'bi bi-pie-chart-fill',
              roles: ['admin', 'supervisor', 'assessor']
            },
            {
              href: '/reports/abstract/water',
              title: 'Water Collections',
              icon: 'bi bi-pie-chart-fill',
              roles: ['admin', 'supervisor']
            },
            {
              href: '/reports/abstract/general',
              title: 'General Collections',
              icon: 'bi bi-pie-chart-fill',
              roles: ['admin', 'supervisor', 'cashier']
            }
          ]
        },
        {
          title: 'Collections',
          icon: 'bi bi-bar-chart-fill',
          roles: ['admin', 'supervisor'],
          child: [
            {
              href: '/reports/collections/cr',
              title: 'Collections Report',
              icon: 'bi bi-bar-chart-fill',
              roles: ['admin', 'supervisor', 'accountant']
            },
            {
              href: '/reports/collections/rdcd',
              title: 'Based on RDCD',
              icon: 'bi bi-bar-chart-fill',
              roles: ['admin', 'supervisor']
            },
            {
              href: '/reports/collections/lo',
              title: 'From Liquidating Officer',
              icon: 'bi bi-bar-chart-fill',
              roles: ['admin', 'supervisor', 'accountant']
            }
          ]
        },
        {
          href: '/reports/accomplishment',
          title: 'Accomplishments',
          icon: 'bi bi-bar-chart-fill',
          roles: ['admin', 'supervisor']
        },
        {
          href: '/reports/corrections',
          title: 'Collector Corrections',
          icon: 'bi bi-bar-chart-fill',
          roles: ['admin', 'supervisor']
        }
      ]
    },
    {
      title: 'Logbook',
      icon: 'bi bi-journal-richtext',
      child: [{
        title: 'Accountable Forms',
        icon: 'bi bi-code-square', // More appropriate icon
        child: [{
          href: '/logbook/accountable-forms/51',
          title: 'Form 51',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/52',
          title: 'Form 52',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/53',
          title: 'Form 53',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/54',
          title: 'Form 54',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/55',
          title: 'Form 55',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/56',
          title: 'Form 56',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/57',
          title: 'Form 57',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/58',
          title: 'Form 58',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/cashticket',
          title: 'Cash Ticket',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/ctc-indv',
          title: 'CTC Individual',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/accountable-forms/ctc-corp',
          title: 'CTC Corporation',
          icon: 'bi bi-textarea-resize', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        ]
      },
      {
        title: 'Bank Deposits',
        icon: 'bi bi-bank2', // More appropriate icon
        child: [{
          href: '/logbook/bank-deposits/general',
          title: 'General',
          icon: 'bi bi-cash-stack', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/bank-deposits/sef',
          title: 'SEF',
          icon: 'bi bi-cash-coin', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        }]
      },
      {
        title: 'Remittances',
        icon: 'bi bi-cash', // More appropriate icon
        child: [{
          href: '/logbook/remittances/cashier',
          title: 'Cashier',
          icon: 'bi bi-wallet-fill', // More appropriate icon
          roles: ['admin', 'liquidating-officer'],
        },
        {
          href: '/logbook/remittances/collector',
          title: 'Collector',
          icon: 'bi bi-coin', // More appropriate icon
          roles: ['admin', 'liquidating-officer', 'collector'],
        }]
      },
      {
        href: '/logbook/liquidating-officer',
        title: 'Liquidating Officer',
        icon: 'bi bi-textarea-resize', // More appropriate icon
        roles: ['admin', 'liquidating-officer'],
      },]
    },
    {
      title: 'Settings',
      icon: 'bi bi-gear',
      child: [
        {
          href: '/settings/concessionaire',
          title: 'Concessionaire Setup',
          icon: 'bi bi-person-gear', // More appropriate icon
          roles: ['admin', 'concessionaire-setup']
        },
        {
          href: '/settings/account',
          title: 'Account Setup',
          icon: 'bi bi-person-fill-gear', // More appropriate icon
          roles: ['admin', 'account-setup']
        },
        {
          href: '/settings/coa',
          title: 'Chart Of Accounts Setup',
          icon: 'bi bi-sliders', // More appropriate icon
          roles: ['admin', 'coa-setup']
        },
        {
          href: '/settings/user',
          title: 'User Setup',
          icon: 'bi bi-person-lines-fill', // More appropriate icon
          roles: ['admin', 'user-setup']
        }



      ]
    },
  ];
  const currentUserRoles = getUserRoles();
  const filterByRole = (items) => {
    return items.filter(item => {
      // If item has children, process them first
      if (item.child) {
        item.child = filterByRole(item.child);

        // Only show parent if:
        // 1. It has visible children (after filtering), AND
        // 2. Either:
        //    a. Parent has no role restrictions, OR
        //    b. Parent's roles include at least one of the user's roles
        const hasVisibleChildren = item.child.length > 0;
        const parentAllowed = !item.roles || item.roles.some(r => currentUserRoles.includes(r));
        return hasVisibleChildren && parentAllowed;
      }

      // For childless items: show if no roles specified or user has required role
      return !item.roles || item.roles.some(requiredRole =>
        currentUserRoles.includes(requiredRole)
      );
    });
  };

  return filterByRole(allItems);
});



const currentRouteTitle = computed(() => {
  const name = route.name?.toString() || ''
  return name.charAt(0).toUpperCase() + name.slice(1)
})
</script>

<style scoped>
.app-container {
  min-height: 100vh;
}


.main-content {
  margin-left: 290px;
  transition: margin-left 0.3s ease;
}

.main-content.collapsed {
  margin-left: 65px;
}

.navbar {
  position: fixed;
  top: 0;
  left: 290px;
  right: 0;
  height: 56px;
  z-index: 999;
  transition: left 0.3s ease;
}

.navbar.collapsed {
  left: 65px;
}

.content-container {
  margin-top: 56px;
  min-height: calc(100vh - 56px);
}

/* Mobile styles */
@media (max-width: 768px) {

  .main-content,
  .main-content.collapsed {
    margin-left: 65px !important;
  }

  .navbar,
  .navbar.collapsed {
    left: 65px !important;
    z-index: 1;
  }

}

/* Transition effects */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Sidebar specific styles */
.v-sidebar-menu.vsm_expanded {
  width: 290px;
}

.v-sidebar-menu.vsm_collapsed {
  width: 65px;
}
</style>